import { Transactions } from "acquaint-api/models/Transactions";
import { SetupFeeGridItem } from "@/types/tenancies/SetupFeeGridItem";
import { TransactionPayment } from "@/types/accounts/TransactionPayment";
import {
  CreditNoteItem,
  CreditNoteRequest,
} from "@/types/accounts/CreditNoteItem";
import { TransactionUnAllocatedCredit } from "@/types/accounts/TransactionUnAllocatedCredit";
import { Products } from "acquaint-api/models/Products";
import {
  TransactionChargeType,
  TransactionCheckerMode,
  TransactionType,
} from "@/enums/accounts/AccountEnums";
import { NewTransactionDetailsProps } from "@/types/accounts/NewTransactionDetailsProps";
import { ProductSelectionProps } from "@/types/accounts/ProductSelectionProps";
import { UnallocatedCreditsModalProps } from "@/types/accounts/UnallocatedCreditsModalProps";
import { SystemContactTypes } from "@/enums/contacts/SystemContactTypes";
import { NewTransactionWizardProps } from "@/types/accounts/NewTransactionWizardProps";
import { TransactionsExtended } from "@/types/accounts//TransactionsExtended";
import {
  RefundModalItem,
  SplitTransactionDto,
  SplitTransactionModalProps,
  TransactionRefundProps,
} from "@/types/accounts/SplitTransactionDto";
import {
  TransferUnallocatedCreditItem,
  TransferUnallocatedCreditsProps,
  TransferUnallocatedCreditsRequest,
} from "@/types/accounts/TransferUnallocatedCredits";
import { CurrentUser } from "../user/CurrentUser";
import { TransferCompanyFunds } from "@/types/accounts/TransferCompanyFunds";

export const defaultTransaction = (): Transactions => ({
  id: 0,
  type: 0,
  status: 0,
  addedDate: "",
  dueDate: "",
  statusDate: "",
  bankingDate: "",
  userID: 0,
  contactID: 0,
  propertyID: 0,
  tenancyID: 0,
  productID: 0,
  taxRateID: 0,
  taxRate: 0.0,
  debit: 0.0,
  credit: 0.0,
  paymentMethodID: 0,
  notes: "",
  statementID: 0,
  invoiceID: 0,
  receiptID: 0,
  billID: 0,
  companyAccount: 0,
  bankAccountID: 0,
  commissionFee: 0.0,
  commissionPercentage: 0.0,
  transferFees: 0,
  statementNotes: "",
  siteID: 0,
  linkedTo: 0,
  linkedCascadeDelete: 0,
  linkGroup: 0,
  paymentCleared: 0,
  paymentClearedDate: "",
  bankReconciliationID: 0,
  includeOnBankRec: 0,
  paymentCollectionID: 0,
  rentDemandSent: 0,
  includeAsDebit: 0,
  transactionGroupID: 0,
  resetBalance: 0,
  version: 0,
  previousStatus: 0,
  periodStartDate: "",
  periodEndDate: "",
  companyID: 0,
  transferDeposit: 0,
  splitID: 0,
  transferOverseasTax: 0,
  creationMethod: 0,
  paymentForID: 0,
  bankAccountBalanceExclude: 0,
  suspenseAccount: 0,
  paymentRunID: 0,
  linkedPropertyID: 0,
  exported: 0,
  bankMatched: 0,
  receivedFromContactID: 0,
  resetFundsHeld: 0,
  resetFundsHeldAmount: 0.0,
  paymentLinkID: 0,
});

export const defaultTransactionExtended = (): TransactionsExtended => ({
  ...defaultTransaction(),
  workOrderId: 0,
  transferred: "",
});

export const defaultSetupFeeGridItem = (): SetupFeeGridItem => ({
  productId: 0,
  productDescription: "",
  taxRate: 0,
  taxRateId: 0,
  defaultAmount: 0,
  net: 0,
  vat: 0,
  gross: 0,
});

export const defaultTransactionPayment = (): TransactionPayment => ({
  amount: 0,
  bankAccount: 0,
  receivedDate: "",
  paymentMethod: -1,
  receivedFrom: -1,
  reference: "",
});

export const defaultTransferCompanyFunds = (): TransferCompanyFunds => ({
  fromBankAccountId: 0,
  toBankAccountId: 0,
  date: "",
  amount: 0,
  statementNotes: "",
});

export const defaultCreditNote = (): CreditNoteItem => ({
  contactId: -1,
  propertyId: -1,
  bankAccountId: -1,
  consolidatedAccount: false,
  amount: 0,
  creditNoteId: 0,
});

const defaultTransactionUnAllocatedCredits =
  (): TransactionUnAllocatedCredit => ({
    propertyId: 0,
    contactId: 0,
    totalDue: 0,
    bankAccount: 0,
    consolidatedAccount: false,
    creditIds: [],
    creditTotal: 0,
  });

const defaultCreditNoteRequest = (): CreditNoteRequest => ({
  contactId: -1,
  propertyId: -1,
  bankAccountId: -1,
  productId: -1,
  date: "",
  amount: 0,
  taxRateId: -1,
  statementNotes: "",
  notes: "",
  transferFundsFrom: -1,
  transferFundsFromBankAccountId: -1,
  payToContact: false,
});

const defaultProducts = (): Products => ({
  id: 0,
  description: "",
  price: 0,
  taxRateID: 4,
  showVATOnStatements: 0,
  costCentreID: 0,
  creditCompanyAccount: 0,
  invoiced: 0,
  transferTo: 0,
  chargeLandlordPrice: 0,
  chargeLandlordProductID: 0,
  overseasTaxDeducted: 0,
  externalAccountCode: "",
  inActive: 0,
});

const defaultNewTransactionDetailsProps = (): NewTransactionDetailsProps => ({
  isEditMode: false,
  contactId: 0,
  defaultPropertyId: 0,
  bankAccountId: 0,
  debitId: 0,
  isCompanyAccount: false,
  isConsolidatedAccount: false,
  isCreditFromCompanyAccount: false,
  transactionType: TransactionType.Debit,
  chargeType: TransactionChargeType.None,
  defaultTransactionData: defaultTransaction(),
});

const defaultProductSelectionProps = (): ProductSelectionProps => ({
  productList: [],
  productId: 0,
  taxRateId: 0,
  amount: 0,
  companyId: 0,
});

const defaultUnallocatedCreditsModalProps =
  (): UnallocatedCreditsModalProps => ({
    contactId: 0,
    propertyId: 0,
    bankAccountId: -1,
    isConsolidatedAccount: false,
    amountDue: 0,
  });

const defaultNewTransactionWizardProps = (): NewTransactionWizardProps => ({
  contactId: 0,
  defaultPropertyId: 0,
  isCompanyAccount: false,
  isConsolidatedAccount: false,
  systemContactType: SystemContactTypes.Applicant,
});

const defaultSplitTransactionDto = (): SplitTransactionDto => ({
  contactId: 0,
  propertyId: 0,
  bankAccountId: 0,
  type: 0,
  date: "",
  paymentMethodId: 0,
  productId: 0,
  taxRateId: 0,
  periodStartDate: "",
  periodEndDate: "",
  totalAmount: 0,
  openAmount: 0,
  closedAmount: 0,
  splitTransactions: [],
});

const defaultSplitTransactionModalProps = (): SplitTransactionModalProps => ({
  transactionId: 0,
  splitId: 0,
  isCompanyAccount: false,
  bankAccountId: 0,
  isConsolidatedAccount: false,
});

const defaultTransactionRefundProps = (): TransactionRefundProps => ({
  contactId: 0,
  propertyId: 0,
  toAccount: 0,
  transactionId: 0,
  refundType: 0,
  isConsolidatedAccount: false,
});

const defaultRefundModalItem = (): RefundModalItem => ({
  modalTitle: "",
  description: "",
  contactId: 0,
  propertyId: 0,
  fromBankAccountId: 0,
  productId: 0,
  paymentMethodId: 0,
  refundAmount: 0,
  companyId: 0,
  date: "",
  statementNotes: "",
  notes: "",
  transferFundsFrom: 0,
  transferFromBankAccountId: 0,
});

const defaultTransferUnallocatedCreditsProps =
  (): TransferUnallocatedCreditsProps => ({
    contactId: 0,
    propertyId: 0,
    bankAccountId: 0,
    isConsolidatedAccount: false,
    transactionId: 0,
    productId: 0,
  });

const defaultTransferUnallocatedCreditItem =
  (): TransferUnallocatedCreditItem => ({
    fromContactId: 0,
    fromPropertyId: 0,
    fromBankAccount: 0,
    fromContactReadyOnly: false,
    fromPropertyReadOnly: false,
    toContactId: 0,
    toPropertyId: 0,
    toBankAccountId: 0,
    productId: 0,
    transferAmount: 0,
    transferDate: "",
    statementNotes: "",
    creditIds: [],
  });

const defaultTransferUnallocatedCreditsRequest =
  (): TransferUnallocatedCreditsRequest => ({
    fromContactId: 0,
    fromPropertyId: 0,
    fromBankAccountId: 0,
    toContactId: 0,
    toPropertyId: 0,
    toBankAccountId: 0,
    transferDate: "",
    transferAmount: 0,
    productId: 0,
    statementNotes: "",
    creditIds: [],
  });

const defaultCurrentUserDetails = (): CurrentUser => ({
  id: 0,
  siteID: 0,
  siteName: "",
  branchName: "",
  countryCodeID: 826,
  initials: "",
  name: "",
  emailAddress: "",
  financialModuleInvoked: false,
  lettingsModuleInvoked: false,
  propertyManagementModuleInvoked: false,
  tapiModuleInvoked: false,
  appMode: 0,
  showSystemReminders: false,
  showContactReminders: false,
  showAppointmentReminders: false,
  showFinancialReminders: false,
  showPropertySalesReminders: false,
  showPropertyLettingsReminders: false,
  showPropertyManagementReminders: false,
  showCertificatesReminders: false,
});

export const AccountDefaults = {
  defaultCurrentUserDetails: defaultCurrentUserDetails,
  defaultTransferUnallocatedCreditsRequest:
    defaultTransferUnallocatedCreditsRequest,
  defaultTransferUnallocatedCreditItem: defaultTransferUnallocatedCreditItem,
  defaultTransferUnallocatedCreditsProps:
    defaultTransferUnallocatedCreditsProps,
  defaultRefundModalItem: defaultRefundModalItem,
  defaultTransactionRefundProps: defaultTransactionRefundProps,
  defaultSplitTransactionModalProps: defaultSplitTransactionModalProps,
  defaultSplitTransactionDto: defaultSplitTransactionDto,
  defaultProducts: defaultProducts,
  defaultNewTransactionDetailsProps: defaultNewTransactionDetailsProps,
  defaultProductSelectionProps: defaultProductSelectionProps,
  defaultUnallocatedCreditsModalProps: defaultUnallocatedCreditsModalProps,
  defaultNewTransactionWizardProps: defaultNewTransactionWizardProps,
  creditNoteItem: defaultCreditNote,
  creditNoteRequest: defaultCreditNoteRequest,
  transaction: defaultTransaction,
  transactionExtended: defaultTransactionExtended,
  setupFeeGridItem: defaultSetupFeeGridItem,
  transactionPayment: defaultTransactionPayment,
  transferCompanyFunds: defaultTransferCompanyFunds,
  transactionUnAllocatedCredits: defaultTransactionUnAllocatedCredits,
};
